import { useState } from 'react'

import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import {
  type QuoteRecipientRow,
  isSigningRow,
  recipientEmailsFor
} from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'
import { AMENDMENT_OUT_FOR_SIGNATURE_SIGNERS_NOTE } from 'Cube/view/layouts/quote/AmendmentOutForSignatureBanner/AmendmentOutForSignatureBanner.constants'
import { NOTIFY_RECIPIENTS_COPY } from './ModalNotifyRecipients.constants'

/**
 * Everything the notify modal does: who is ticked, the note being written, and
 * what sending means. The modal itself is then only the markup.
 */
export const useModalNotifyRecipients = ({
  rows
}: {
  rows: QuoteRecipientRow[]
}) => {
  const ctx = useCubeContext()
  const quoteId = ctx.queries.rawData.data.common.id
  const reloadQuote = ctx.mutators.external.in.core

  const { displayNotification } = useNotifications()

  const notifyRecipientsMutation = useMutation(
    dashboard20240730Client.postQuoteNotifyRecipients
  )

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [message, setMessage] = useState<string>('')

  // Writing to a signer makes the quote ready to sign, which can't happen while another
  // amendment of the schedule holds the signing slot. CC recipients can still be notified.
  const canNotifySigners =
    ctx.queries.availableFeatures.quote.readyToSign.available.enabled
  const selectableRows = canNotifySigners
    ? rows
    : rows.filter(row => !isSigningRow(row))

  const recipients = recipientEmailsFor(selectableRows, selectedIds)

  // Writing to a signer is what moves the quote on to the signing flow, so the
  // editor has to reload to catch up. Notifying only cc recipients changes
  // nothing about the quote's status.
  const notifiesASigner = selectableRows.some(
    row => selectedIds.includes(row.id) && isSigningRow(row)
  )

  const onSubmit = async () => {
    try {
      await notifyRecipientsMutation.mutateAsync({
        quoteId,
        body: {
          recipients,
          emailMessage: message.trim() || undefined
        }
      })
    } catch {
      displayNotification(NOTIFY_RECIPIENTS_COPY.errorToast, { type: 'error' })

      // A refusal usually means the quote moved on behind us - most often it was superseded by
      // another amendment of the same schedule. Reload so the editor says so.
      await reloadQuote()

      return false
    }

    displayNotification(NOTIFY_RECIPIENTS_COPY.successToast, {
      type: 'success'
    })

    // The reload swaps the card out from under this modal, so confirm the send
    // first - otherwise the toast races the unmount.
    if (notifiesASigner) {
      await reloadQuote()
    }

    return true
  }

  return {
    selectedIds,
    setSelectedIds,
    message,
    setMessage,
    isSubmitting: notifyRecipientsMutation.isPending,
    hasNoRecipients: recipients.length === 0,
    signersUnavailableNote: canNotifySigners
      ? undefined
      : AMENDMENT_OUT_FOR_SIGNATURE_SIGNERS_NOTE,
    onSubmit
  }
}
